'use client';

import React, { useEffect } from 'react';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { useAnalytics } from 'src/hooks/analytics';
import { Drawer, Tabs, TabSet, Tab, TabPanel } from 'src/components';
import { Search } from 'src/components/PulseUI';
import { IconButton } from 'src/components/PulseUI/IconButton';
import {
  AnalyticsTerritoryPanel,
  AnalyticsRegionPanel,
  AnalyticsStatePanel,
} from './components';

const AnalyticsTerritoryDrawer = () => {
  const { isDrawerOpen, closeDrawer } = useDrawer();
  const {
    geographySearch,
    setGeographySearch,
    tempSelectedGeographies,
    initializeTempSelectionsFromCurrent,
    applyTempGeographySelections,
    clearTempSelectedGeographies,
  } = useAnalytics() || {};

  const open = isDrawerOpen('analytics-territory-drawer');

  useEffect(() => {
    if (open && initializeTempSelectionsFromCurrent) {
      initializeTempSelectionsFromCurrent();
    }
  }, [open, initializeTempSelectionsFromCurrent]);

  const handleClose = () => {
    closeDrawer('analytics-territory-drawer');
    setGeographySearch('');
  };

  const handleCancel = () => {
    initializeTempSelectionsFromCurrent();
    handleClose();
  };

  const handleConfirm = () => {
    applyTempGeographySelections();
    handleClose();
  };

  const handleClearAll = () => {
    clearTempSelectedGeographies();
  };

  if (!open) return null;

  return (
    <Drawer
      isOpen={open}
      onClose={handleClose}
      anchor="left"
      size="base2"
      variant="persistent"
      className="z-50 absolute left-0 top-0 bottom-0"
    >
      {/* Drawer Header */}
      <Drawer.Header className="bg-sky-100 text-primary border-b-0">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <p className="font-bold text-lg">Select Geography</p>
          </div>
          <div className="flex items-center gap-2">
            <IconButton
              variant="plain"
              color="neutral"
              size="sm"
              onClick={handleCancel}
              icon={faXmark}
              className="text-primary hover:bg-sky-200"
            />
          </div>
        </div>
      </Drawer.Header>

      {/* Drawer Content */}
      <Drawer.Content className="bg-neutral-050 ">
        <Tabs className="h-full flex flex-col">
          <div className="flex-none">
            <TabSet id="territory-tabs" className="w-full">
              <Tab
                tabSetId="territory-tabs"
                index={0}
                className="w-full flex items-center justify-center"
              >
                Territories
              </Tab>
              <Tab
                tabSetId="territory-tabs"
                index={1}
                className="w-full flex items-center justify-center"
              >
                Regions
              </Tab>
              <Tab
                tabSetId="territory-tabs"
                index={2}
                className="w-full flex items-center justify-center"
              >
                States
              </Tab>
            </TabSet>
          </div>
          <div className="p-4 border-b border-neutral-200 flex items-center gap-2">
            <Search
              value={geographySearch}
              onSearch={(value) => setGeographySearch?.(value)}
              placeholder="Search geographies..."
              size="sm"
              className="w-full rounded-full"
              autoFocus
            />
            <button
              className="text-xs text-red-600 font-medium hover:text-red-700 disabled:text-neutral-400 disabled:cursor-not-allowed whitespace-nowrap"
              onClick={handleClearAll}
              disabled={
                !tempSelectedGeographies || tempSelectedGeographies.length === 0
              }
            >
              Clear All
            </button>
          </div>

          <TabPanel
            tabSetId="territory-tabs"
            index={0}
            className="flex flex-col flex-1 min-h-0"
          >
            <AnalyticsTerritoryPanel />
          </TabPanel>

          <TabPanel
            tabSetId="territory-tabs"
            index={1}
            className="flex flex-col flex-1 min-h-0"
          >
            <AnalyticsRegionPanel />
          </TabPanel>

          <TabPanel
            tabSetId="territory-tabs"
            index={2}
            className="flex flex-col flex-1 min-h-0"
          >
            <AnalyticsStatePanel />
          </TabPanel>
        </Tabs>
      </Drawer.Content>
      <Drawer.Footer className="px-6 py-4 bg-white border-t border-neutral-200">
        <div className="w-full flex justify-between items-center gap-2">
          <button
            className="w-full rounded-full px-4 py-2 bg-white text-sm font-medium text-primary border border-neutral-200 hover:bg-neutral-100"
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className="w-full rounded-full px-4 py-2 bg-sky-600 text-sm font-medium text-white hover:bg-sky-700"
            onClick={handleConfirm}
          >
            Confirm
          </button>
        </div>
      </Drawer.Footer>
    </Drawer>
  );
};

export default AnalyticsTerritoryDrawer;
